// Thin wrappers over the standalone Model Hub signup endpoints
// (benchling/model_hub/views/verification.py and signup.py).
//
// These are unauthenticated, pre-session POSTs. We use net.postJSONPromise (the idiomatic
// wrapper, which injects the CSRF header via registered header generators) and additionally pass
// csrf_token in the body, mirroring client/llm/chat-library/auth/AITrialSignupModal.tsx.

import * as csrf from 'CSRF';
import * as net from 'util/Net';

import type {EmailType} from './constants';

interface SignupApiResponse {
  success: boolean;
  email_type?: EmailType;
  error?: string;
  errors?: Record<string, Array<string>>;
}

// The jqXHR rejection surfaced by $.ajax / postJSONPromise. Fields are optional because a network
// error may produce none of them.
export interface SignupApiError {
  status?: number;
  responseJSON?: SignupApiResponse;
}

/**
 * Whether a caught value is a request rejection from these endpoints (a jqXHR-like object), as
 * opposed to an unexpected programming error that should propagate. postJSONPromise rejects with
 * the jqXHR object, so real API failures always carry `status` and/or `responseJSON`.
 */
export function isSignupApiError(value: unknown): value is SignupApiError {
  if (typeof value !== 'object' || value == null) {
    return false;
  }
  const candidate = value as SignupApiError;
  return candidate.status != null || candidate.responseJSON != null;
}

function post(url: string, data: Record<string, unknown>): Promise<SignupApiResponse> {
  return net.postJSONPromise({url, data: {...data, csrf_token: csrf.token}});
}

export function sendCode(email: string, termsAcceptedAt: string | null): Promise<SignupApiResponse> {
  return post('/signup/send-code', {email, terms_accepted_at: termsAcceptedAt});
}

export function verifyCode(email: string, code: string): Promise<SignupApiResponse> {
  return post('/signup/verify-code', {code, email});
}

export function resendCode(email: string): Promise<SignupApiResponse> {
  return post('/signup/resend-code', {email});
}

export function cancelGoogleOauthSignup(): Promise<SignupApiResponse> {
  return post('/signup', {action: 'cancel_google_oauth'});
}

export function prepareGoogleOauthSignup(termsAcceptedAt: string | null): Promise<SignupApiResponse> {
  return post('/signup', {action: 'prepare_google_oauth', terms_accepted_at: termsAcceptedAt});
}

export interface SignupPayload {
  authMethod: 'email' | 'google';
  email: string;
  code?: string;
  firstName: string;
  lastName: string;
  password?: string;
}

export function signup({
  authMethod,
  email,
  code,
  firstName,
  lastName,
  password,
}: SignupPayload): Promise<SignupApiResponse> {
  // No username: the backend derives a handle from the email (see ModelHubRegistrationForm).
  const data: Record<string, unknown> = {
    code,
    email,
    first_name: firstName,
    last_name: lastName,
    password,
  };
  if (authMethod === 'google') {
    data.action = 'create_google_oauth';
    delete data.code;
    delete data.password;
  }
  return post('/signup', data);
}

/** Extract the best human-readable message from a rejected signup request. */
export function getSignupErrorMessage(
  error: SignupApiError,
  fallback = 'Something went wrong. Please try again.'
): string {
  const responseJson = error.responseJSON;
  if (responseJson?.error != null && responseJson.error !== '') {
    return responseJson.error;
  }
  return fallback;
}
